import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Collapse, Paper, Theme, Typography, useMediaQuery, useTheme, SxProps } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../hooks';
import { entriesAddMany, selectActiveRepositoryId, selectAllRepositories, selectAllRepositoryEntries, selectLocationId } from '../containers/contentSlice';
import { Link } from 'react-router-dom';
import AddRepositoryDialog from './AddRepositoryDialog';
import { assignRandomId } from '../common/util';
import { selectIsAuthenticated } from '../containers/authSlice';
import { selectCurrentLocationEntriesMaxRank } from '../containers/repositoryEntryViewSlice';
import { useEntryImportMutation } from '../services/records';
import AddIcon from '@mui/icons-material/Add';
import { isAddRepositoryDialogOpenSetState, selectIsAddRepositoryDialogOpen } from '../containers/formDataSlice';
import { AnyPropertyDefinitionEntityType, EntityType } from '../types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import StorageIcon from '@mui/icons-material/Storage';
import { RepositoryListItem, useCreateRepositoryMutation, useLoadRepositoriesQuery } from '../services/repositoryEntries';
import { RepositoryCreateDto } from '../services/repositoryEntries';
import useAuth from './Auth';
import ReactGA from 'react-ga4';
import { usePropertyDefinitionCreateMutation } from '../services/propertyDefinitions';

import { bookmarkColors } from '../constants/bookmarkColors';

const TRANSITION_DURATION = '200ms';
const TRANSITION_DELAY = '200ms';
const REPOSITORIES_PANEL_COLLAPSED_STORAGE_KEY = 'repositoriesPanel.isCollapsed';

const getStoredCollapsedState = (): boolean | null => {
	if (typeof window === 'undefined') {
		return null;
	}
	const storedValue = window.localStorage.getItem(REPOSITORIES_PANEL_COLLAPSED_STORAGE_KEY);
	if (storedValue === 'true') {
		return true;
	}
	if (storedValue === 'false') {
		return false;
	}
	return null;
};

interface RepositoriesPanelProps {
	forceCollapse: boolean;
}

interface RepositoriesPaneltemProps {
	children: React.ReactNode;
	isHidden: boolean;
	isCollapsed: boolean;
	onClick?: () => void;
	sx?: SxProps<Theme>;
	component?: React.ElementType;
}

function RepositoriesPaneltem({children, isHidden, isCollapsed, onClick, sx, component}: RepositoriesPaneltemProps) {
	return <Box
		component={component || Paper}
		onClick={onClick}
		sx={(theme) => {
			return {
				display: 'inline-flex',
				alignItems: 'center',
				cursor: 'pointer',
				pr: 2,
				pl: 1.5,
				py: 1.25,
				my: '2px',
				borderRadius: theme.spacing(3),
				borderTopLeftRadius: 0,
				borderBottomLeftRadius: 0,
				width: 'fit-content',
				maxWidth: 'min(260px, 60vw)',
				transform: isHidden ? 'translateX(-140%)' : 'translateX(0)',
				opacity: isHidden ? 0 : 1,
				pointerEvents: isHidden ? 'none' : 'auto',
				transition: theme.transitions.create(['transform', 'opacity', 'padding-left'], {
					duration: TRANSITION_DURATION,
					delay: TRANSITION_DELAY,
				}),
				'&:hover': {
					transform: isHidden
						? 'translateX(-140%)'
						: 'translateX(0)',
					pl: 3,
					scale: 1.05,
				},
				...(isCollapsed ? { boxShadow: 'none' } : {}),
				...(sx as any), // this is risky, but needed for overriding transform for add collection button
			};
		}}
	>
		{children}
	</Box>;
}

function RepositoriesPanel({forceCollapse = false}: RepositoriesPanelProps) {
	const isAddRepositoryDialogOpen = useAppSelector(selectIsAddRepositoryDialogOpen);
	const [createRepositoryMutation] = useCreateRepositoryMutation();
	const [propertyDefinitionCreateMutation] = usePropertyDefinitionCreateMutation();
	const isSmScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
	const dispatch = useAppDispatch();
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const currentMaxRank = useAppSelector(selectCurrentLocationEntriesMaxRank);
	const parentId = useAppSelector(selectLocationId);
	const activeRepositoryId = useAppSelector(selectActiveRepositoryId);
	const [entryImportMutation] = useEntryImportMutation();
	const locationId = useAppSelector(selectLocationId);
	const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
		if (forceCollapse) {
			return true;
		}
		return getStoredCollapsedState() ?? false;
	});
	const { userProfile } = useAuth();

	const { data: remoteRepositories = [], isSuccess: isRepositoriesSuccess } = useLoadRepositoriesQuery(undefined, { skip: !userProfile });
	const localRepositories = useAppSelector(selectAllRepositories);
	const localRepositoryEntries = useAppSelector(selectAllRepositoryEntries);

	const loadReppositoriesLocal = useCallback(() => {
		const entryCountsByRepositoryId = localRepositoryEntries.reduce((acc, entry) => {
			acc[entry.repositoryId] = (acc[entry.repositoryId] || 0) + 1;
			return acc;
		}, {} as Record<string, number>);
		return localRepositories.map((repository) => ({
			...repository,
			entryCount: entryCountsByRepositoryId[repository.id] || 0,
		}));
	}, [localRepositories, localRepositoryEntries]);

	const repositoryListItems: RepositoryListItem[] = useMemo(() => {
		return isRepositoriesSuccess ? remoteRepositories : loadReppositoriesLocal();
	}, [isRepositoriesSuccess, remoteRepositories, localRepositories, localRepositoryEntries]);

	useEffect(() => {
		if (forceCollapse) {
			setIsCollapsed(true);
			return;
		}

		const storedCollapsedState = getStoredCollapsedState();
		if (storedCollapsedState !== null) {
			setIsCollapsed(storedCollapsedState);
			return;
		}

		setIsCollapsed(isSmScreen && repositoryListItems.length > 2);
	}, [forceCollapse, isSmScreen, repositoryListItems.length]);

	useEffect(() => {
		if (forceCollapse || typeof window === 'undefined') {
			return;
		}
		window.localStorage.setItem(REPOSITORIES_PANEL_COLLAPSED_STORAGE_KEY, String(isCollapsed));
	}, [forceCollapse, isCollapsed]);

	const theme = useTheme();
	const top = !forceCollapse ? 10 : 0.5;

	const handleAddDialogSubmit = useCallback((formData: any) => {
		const repositoryCount = isRepositoriesSuccess ? remoteRepositories.length : localRepositories.length;
		const initialColor = bookmarkColors[repositoryCount % bookmarkColors.length];

		const newEntryPayload = assignRandomId({
			...formData,
			parentId: parentId ?? null,
			rank: currentMaxRank + 1,
		});

		// let's create a text property called 'title' by default
		const labelProperty = assignRandomId({
			type: EntityType.PropertyDefinitionString as AnyPropertyDefinitionEntityType,
			parentId: newEntryPayload.id,
			label: 'Title',
			slug: 'title',
			required: false,
		});

		if (isAuthenticated) {
			createRepositoryMutation({
				...formData,
				rank: currentMaxRank + 1,
				isBookmarked: true,
				color: initialColor,
			} as RepositoryCreateDto).unwrap().then((repository) => {
				propertyDefinitionCreateMutation({...labelProperty, parentId: repository.id});
				dispatch(isAddRepositoryDialogOpenSetState(false));
			});
		} else {
			dispatch(entriesAddMany([{ ...newEntryPayload, isBookmarked: true, color: initialColor } as any, labelProperty as any]));
			dispatch(isAddRepositoryDialogOpenSetState(false));
		}
	}, [dispatch, isAuthenticated, currentMaxRank, parentId, isRepositoriesSuccess, remoteRepositories, localRepositories, createRepositoryMutation, entryImportMutation]);

	const handleAddRepositoryClick = () => {
		dispatch(isAddRepositoryDialogOpenSetState(true));
		ReactGA.event({
			category: 'UI',
			action: 'AddRepositoryClicked',
		});
	};

	return (
		<Box
			sx={(theme) => ({
				position: 'fixed',
				top: theme.spacing(!repositoryListItems.length || activeRepositoryId ? top : 10),
				transition: theme.transitions.create(['top'], {
					duration: TRANSITION_DURATION,// theme.transitions.duration.shortest,
					delay: isCollapsed ? TRANSITION_DELAY : '0ms',
				}),
				left: theme.spacing(2),
				zIndex: theme.zIndex.appBar + 1,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'flex-start',
				// gap: 0.5,
				marginLeft: theme.spacing(-2),
				// make click go through items
				pointerEvents: 'none',
			})}
		>
			<Collapse in={!forceCollapse} sx={{transitionDelay: isCollapsed ? TRANSITION_DELAY : '0ms', transitionDuration: TRANSITION_DURATION}}>
				<RepositoriesPaneltem component={Paper} isHidden={forceCollapse} isCollapsed={true} onClick={() => setIsCollapsed(!isCollapsed)} sx={{
					transform: isCollapsed ? 'translateX(calc(0))' : 'translateX(calc(-100% + 48px))',
					color: 'text.primary', 
					'&:hover': {
						pl: 3,
						scale: 1.05,
						'& .MuiTypography-root': {
							opacity: 1,
						},
					},
					display: 'flex',
					gap: 1,
				}} >
					<StorageIcon fontSize='small' sx={{color: 'text.secondary'}}/>
					{isCollapsed ? <ExpandMoreIcon sx={{color: 'text.secondary'}} /> : <ExpandLessIcon sx={{color: 'text.secondary'}} />}
				</RepositoriesPaneltem>
			</Collapse>
			{repositoryListItems.map((repository: RepositoryListItem) => {
				const isActive = repository.id === activeRepositoryId;
				const isHidden = (isCollapsed || !repository.isBookmarked) && !isActive;
				// if not already on the repository page, navigate to it, otherwise navigate to root
				const navigateToPath = repository.id === locationId ? '/' : `/collection/${repository.id}`;
				const bgColor = repository.color ?? theme.palette.grey[200];
				const color = theme.palette.getContrastText(bgColor);

				return (
					<Collapse key={repository.id} in={!isHidden} sx={{transitionDelay: isHidden ? TRANSITION_DELAY : '0ms', transitionDuration: TRANSITION_DURATION}}>
						<Box component={navigateToPath ? Link : 'div'} to={navigateToPath} sx={{outline: 'none'}}>
							<RepositoriesPaneltem isHidden={isHidden} isCollapsed={isCollapsed} sx={{bgcolor: bgColor, color: color}} >
								<Typography
									variant="button"
									sx={{
										textTransform: 'none',
										fontSize: 12,
										whiteSpace: 'nowrap',
										userSelect: 'none',
									}}
								>
									{repository.title}
								</Typography>
							</RepositoriesPaneltem>
						</Box>
					</Collapse>
				);
			})}
			{/* add new repository button that looks like another list item */}
			<RepositoriesPaneltem isHidden={isCollapsed} isCollapsed={false} onClick={handleAddRepositoryClick} sx={{
				transform: isCollapsed ? 'translateX(calc(-100%))' : 'translateX(calc(-100% + 48px))',
				bgcolor: 'action.hover',
				color: 'text.primary', 
				'&:hover': {
					transform: 'translateX(0)',
					pl: 3,
					scale: 1.05,
					'& .MuiTypography-root': {
						opacity: 1,
					},
				},
			}} >
				<Typography variant="button" sx={{textTransform: 'none', fontSize: 12, mr: 0.5, opacity: 0}}>New collection</Typography>
				<AddIcon sx={{color: 'text.secondary'}} />
			</RepositoriesPaneltem>
			<AddRepositoryDialog
				open={isAddRepositoryDialogOpen}
				onClose={() => dispatch(isAddRepositoryDialogOpenSetState(false))}
				onSubmit={handleAddDialogSubmit}
			/>
		</Box>
	);
}

export default RepositoriesPanel;

